import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FaHourglassEnd, FaSearch, FaRedo } from 'react-icons/fa';
import PropertyDetailsView from '../../Components/PropertyDetailsView/PropertyDetailsView';
import style from './TemporaryProperty.module.css';

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Fichas privadas compartidas por un agente: no deben indexarse ni filtrar la URL por Referer.
function usePrivatePageMeta() {
  useEffect(() => {
    const tags = [['robots', 'noindex, nofollow, noarchive'], ['referrer', 'no-referrer']].map(([name, content]) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    });
    return () => tags.forEach(tag => tag.remove());
  }, []);
}

function StatusScreen({ icon, title, text, action }) {
  return (
    <div className={style.status}>
      <span className={style.statusIcon} aria-hidden="true">{icon}</span>
      <h1>{title}</h1>
      <p>{text}</p>
      <div className={style.statusActions}>
        {action}
        <Link to="/" className={style.secondary}><FaSearch aria-hidden="true" /> Ver propiedades</Link>
      </div>
    </div>
  );
}

const TemporaryProperty = () => {
  const { id } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [now, setNow] = useState(Date.now());
  const [attempt, setAttempt] = useState(0);
  usePrivatePageMeta();

  useEffect(() => {
    if (!UUID_V4.test(id || '')) {
      setState({ status: 'notFound' });
      return undefined;
    }
    let active = true;
    setState({ status: 'loading' });
    axios.get(`/temporary-properties/${encodeURIComponent(id)}`)
      .then(({ data }) => {
        if (!active) return;
        if (data?.success === true && data?.data?.agent &&
            data.data.isTemporary === true && Number.isFinite(Date.parse(data.data.expiresAt))) {
          setNow(Date.now());
          setState({ status: 'ready', property: data.data });
        } else {
          setState({ status: 'error' });
        }
      })
      .catch((error) => {
        if (!active) return;
        const code = error?.response?.status;
        if (code === 410) setState({ status: 'expired' });
        else if (code === 400 || code === 404) setState({ status: 'notFound' });
        else setState({ status: 'error' });
      });
    return () => { active = false; };
  }, [id, attempt]);

  useEffect(() => {
    if (state.status !== 'ready') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [state.status]);

  const retry = useCallback(() => setAttempt(a => a + 1), []);

  useEffect(() => {
    const revalidate = () => {
      setState({ status: 'loading' });
      retry();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [retry]);

  useEffect(() => {
    if (state.status !== 'ready') return undefined;
    const remaining = Date.parse(state.property.expiresAt) - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => {
      setState({ status: 'loading' });
      retry();
    }, remaining);
    return () => clearTimeout(timer);
  }, [state, retry]);

  if (state.status === 'loading') {
    return (
      <div className={style.status} role="status">
        <span className={style.spinner} aria-hidden="true" />
        <p>Cargando propiedad…</p>
      </div>
    );
  }

  const expiresAt = state.property ? new Date(state.property.expiresAt).getTime() : 0;
  const expired = state.status === 'expired' || (state.status === 'ready' && expiresAt <= now);

  if (expired) {
    return (
      <StatusScreen icon={<FaHourglassEnd />} title="Este link ya venció"
        text="Las fichas compartidas están disponibles por 48 horas. Pedile un link nuevo a tu agente o explorá nuestras propiedades." />
    );
  }
  if (state.status === 'notFound') {
    return (
      <StatusScreen icon={<FaSearch />} title="No encontramos esta ficha"
        text="El link no existe o ya no está disponible. Revisá que esté completo." />
    );
  }
  if (state.status === 'error') {
    return (
      <StatusScreen icon={<FaRedo />} title="No pudimos cargar la propiedad"
        text="Hubo un problema de conexión. Intentá nuevamente en unos segundos."
        action={<button type="button" className={style.primary} onClick={retry}><FaRedo aria-hidden="true" /> Reintentar</button>} />
    );
  }

  const { property } = state;
  return <PropertyDetailsView key={property.id} property={property} />;
};

export default TemporaryProperty;
